import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import {
  MAX_RETRIES,
  MFEPackageIds,
  NO_MANIFEST_PACKAGES,
} from '@interfaces/open-choice/mfe-details.enum';
import { Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { environment } from '@src/environments';
import * as fromRootStore from '@store/index';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MfeLoadService, MfeScriptExistsError } from 'vg-interaction-model';

function normalizeBase(base?: string): string | undefined {
  if (!base) return undefined;
  return base.endsWith('/') ? base : `${base}/`;
}

function joinUrl(base: string, path: string): string {
  return `${normalizeBase(base)}${path.replace(/^\//, '')}`;
}

function selectAssetFromManifest(
  base: string,
  manifest: Record<string, string> | null | undefined,
): string | undefined {
  if (!manifest || typeof manifest !== 'object') return undefined;
  const entries = Object.values(manifest);
  if (!entries.length) return undefined;

  const main = entries.find((f) => /(^|\/)main\.[\w-]+\.(js|mjs)$/.test(f));
  const chosen = main ?? entries[0];
  return joinUrl(base, chosen);
}

export const mfeResolver = (mfe: MFEPackageIds): ResolveFn<void> => {
  return (): Observable<void> => {
    const mfeLoadService: MfeLoadService = inject(MfeLoadService);
    const store: Store<fromRootStore.State> = inject(Store<fromRootStore.State>);
    const http = inject(HttpClient);

    const mfeBaseUrl = normalizeBase(environment.mfe[mfe]);
    const fallbackUrl = environment.mfe[`${mfe}-fallback`];
    // Prod and test builds hardcode their own API hosts, so the fallback must serve the build for
    // the running env — a prod bundle in a lower env would call prod APIs (e.g. personal1.vanguard.com).
    const localAssetsEnv = environment.isProduction ? 'prod' : 'test';
    const localAssetsUrl = `/assets/MFE/${localAssetsEnv}/${mfe}/main.js`;

    const loadMfe = (url: string | undefined, isLocalAssetsFallback = false): Observable<void> => {
      if (!url && !fallbackUrl && !isLocalAssetsFallback) {
        store.dispatch(
          fromRootStore.LogError({
            payload: {
              eventId: 'mfe-resolver--no-url',
              message: `MFE resolver has no valid URL (base or fallback) for "${mfe}"`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );
        return of(undefined);
      }
      /* istanbul ignore next - nullish coalescing operator branch coverage limitation */
      const finalUrl = isLocalAssetsFallback ? localAssetsUrl : (url ?? fallbackUrl);
      return from(
        mfeLoadService.loadMFE(mfe, finalUrl!, MAX_RETRIES, true, false, fallbackUrl),
      ).pipe(
        map(() => {
          store.dispatch(
            fromRootStore.LogInfo({
              payload: {
                eventId: 'mfe-resolver--mfe-load-success',
                message: `MFE ${mfe} loaded from ${finalUrl}${isLocalAssetsFallback ? ' (local assets fallback)' : ''}`,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
          return undefined;
        }),
      );
    };

    const isNetworkError = (error: unknown): boolean => {
      if (!error) return false;
      const errorString = error.toString().toLowerCase();
      return (
        errorString.includes('http') ||
        errorString.includes('xhr') ||
        errorString.includes('network') ||
        errorString.includes('fetch') ||
        errorString.includes('timeout') ||
        (error as any)?.status >= 400
      );
    };

    const createErrorHandler = (
      hasTriedLocalAssets: boolean,
    ): ((error: unknown) => Observable<void>) => {
      return (error: unknown): Observable<void> => {
        if (error instanceof MfeScriptExistsError) {
          return of(undefined);
        }

        // Try local assets as fallback on network errors
        if (!hasTriedLocalAssets && isNetworkError(error)) {
          store.dispatch(
            fromRootStore.LogInfo({
              payload: {
                eventId: 'mfe-resolver--trying-local-assets',
                message: `MFE resolver encountered network error for ${mfe}, attempting local assets fallback`,
              },
            }),
          );
          return loadMfe(localAssetsUrl, true).pipe(catchError(createErrorHandler(true)));
        }

        store.dispatch(
          fromRootStore.LogError({
            payload: {
              eventId: 'mfe-resolver--mfe-load-error',
              message: `MFE resolver failed to load MFE ${mfe} with error: ${JSON.stringify(sanitizeError(error))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        );

        if (mfe === MFEPackageIds.SETTLEMENT_FUND_PKG) {
          store.dispatch(fromRootStore.Next());
        } else if (
          mfe === MFEPackageIds.PORTFOLIO_BUILDER_PKG ||
          mfe === MFEPackageIds.RISK_QUIZ_PKG_ROE
        ) {
          store.dispatch(fromRootStore.rejectDAOffer());
          store.dispatch(fromRootStore.UpdateDAComponentFailure({ payload: true }));
          store.dispatch(fromRootStore.Go({ path: ['secure/minor-transfer/review'] }));
        }

        return of(undefined);
      };
    };

    return defer(() => {
      if (NO_MANIFEST_PACKAGES.has(mfe)) {
        const directUrl = environment.mfe[mfe];
        return loadMfe(directUrl).pipe(catchError(createErrorHandler(false)));
      }

      if (!mfeBaseUrl) {
        return loadMfe(fallbackUrl).pipe(catchError(createErrorHandler(false)));
      }

      const headers = new HttpHeaders({ 'Cache-Control': 'no-cache' });

      const manifestUrl = joinUrl(mfeBaseUrl, 'manifest.json');

      return http.get<Record<string, string>>(manifestUrl, { headers }).pipe(
        map((manifest) => selectAssetFromManifest(mfeBaseUrl, manifest) ?? fallbackUrl),
        switchMap((finalUrl) => loadMfe(finalUrl)),
        catchError(createErrorHandler(false)),
      );
    });
  };
};
